import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { DateTime } from "luxon";

import { useDataApi } from "@core/apis";
import { getPropAIOrganizationListQueryOptions } from "@core/apis/PropAI/queries";
import { patchCachedSubstationSensor, useFlexStationEventLog } from "@core/apis/shared";

import { PropAIApi } from "../propai.api";
import {
  getFlexstationClimateSeriesOptions,
  getFlexStationsPerSubstationOptions,
} from "../queries";

// --- FlexStation / Organization ---

export const useGetFlexStations = (substationUid?: string) => {
  const dataApi = useDataApi() as PropAIApi;

  return useQuery({
    ...getFlexStationsPerSubstationOptions(dataApi, substationUid),
    enabled: !!substationUid,
  });
};

/**
 * PropAI flexstation event log.
 *
 * Resolves the substation's first flexstation (PropAI)
 * and delegates fetching to the shared, input-driven `useFlexStationEventLog`.
 * platform-web skips this wrapper and calls the shared hook directly with a
 * known `flexStationUid`/`networkUid`.
 */
export const usePropAiFlexStationEventLog = (substationId?: string) => {
  const { data: flexStations, isLoading: isLoadingFlexStations } = useGetFlexStations(substationId);
  const flexStationUid = flexStations?.[0]?.uid;

  const query = useFlexStationEventLog({ substationUid: substationId, flexStationUid });

  return {
    ...query,
    flexStationUid,
    isLoading: isLoadingFlexStations || query.isLoading,
  };
};

export const useOrganization = () => {
  const dataApi = useDataApi() as PropAIApi;
  //! Tanstack router is only in PropAI - for now
  const { organizationId } = useParams({ strict: false });
  return useQuery({
    ...getPropAIOrganizationListQueryOptions(dataApi),
    enabled: !!organizationId && !!dataApi,
    select(data) {
      return data?.find((org) => org.uid === organizationId);
    },
  });
};

// --- Sensor Hooks ---

// Sensor reads now use the shared API boundary.
// Re-exported for existing `@core/apis/PropAI` imports.
export { useAverageSensorData, useSensorReadings, useSubstationSensors } from "@core/apis/shared";

export const useUpdateSensorMetaData = () => {
  const dataApi = useDataApi() as PropAIApi;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      flexstationUid,
      sensorUid,
      apartment,
      enabled,
      floor,
      streetAddress,
    }: {
      substationUid?: string;
      flexstationUid: string;
      sensorUid: string;
      apartment: string;
      enabled: boolean;
      floor: number;
      streetAddress: string;
    }) =>
      dataApi.updateSensorMetaData(
        flexstationUid,
        sensorUid,
        apartment,
        enabled,
        floor,
        streetAddress
      ),
    onSuccess: (_data, variables) => {
      if (!variables.substationUid) return;
      patchCachedSubstationSensor(queryClient, variables.substationUid, variables.sensorUid, {
        apartment: variables.apartment,
        enabled: variables.enabled,
        floor: variables.floor,
        street_address: variables.streetAddress,
      });
      void queryClient.invalidateQueries({
        queryKey: ["sensors", variables.substationUid],
      });
    },
  });
};

export const useFlexstationClimateSeries = (
  flexstationUid: string | null | undefined,
  dateRange: { startDate: DateTime | null; endDate: DateTime | null }
) => {
  const dataApi = useDataApi() as PropAIApi;

  return useQuery(
    getFlexstationClimateSeriesOptions(
      dataApi,
      flexstationUid,
      dateRange.startDate,
      dateRange.endDate
    )
  );
};
